import depsloader from "../../../depsloader";

const DEFAULT_SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const DEFAULT_REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function runtime() {
  const r = (typeof window !== "undefined" && window.ScreenworkMapsRuntime) || {};
  return {
    key: typeof r.geocoderKey === "string" ? r.geocoderKey.trim() : "",
    searchUrl: r.geocoderUrl || DEFAULT_SEARCH_URL,
    reverseUrl: r.reverseGeocoderUrl || DEFAULT_REVERSE_URL
  };
}

function cancelledError() {
  const err = new Error("geocoder request cancelled");
  err.cancelled = true;
  return err;
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function str(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  isCancelled(err) {
    return !!(err && (err.cancelled || this.axios.isCancel(err)));
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip) {
      s = g.zip;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Legacy-Fallback fuer Antworten ohne bekannten properties.type
  _legacyFields(pr, g) {
    let name = str(pr.name);
    let street = str(pr.street);

    if (pr.osm_key == "highway" && !street) {
      street = name;
      name = null;
    }

    if (street) {
      g.street = street;
      if (str(pr.housenumber)) {
        g.number = pr.housenumber;
      }
    }

    if (str(pr.postcode)) {
      g.zip = pr.postcode;
    }

    if (str(pr.city)) {
      g.city = pr.city;
      if (name && name != pr.city) {
        if (pr.osm_key == "boundary" || (pr.osm_key == "place" && pr.osm_value == "suburb")) {
          g.district = name;
        }
      }
    } else if (name && (pr.osm_key == "place" || pr.osm_key == "boundary")) {
      g.city = name;
    }
  }

  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const pr = l.properties;
    const lng = Number(l.geometry.coordinates[0]);
    const lat = Number(l.geometry.coordinates[1]);
    if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) {
      return null;
    }

    const g = {};
    const type = str(pr.type);
    const name = str(pr.name);
    const city = str(pr.city);
    const district = str(pr.district) && pr.district != pr.city ? pr.district : null;

    if (str(pr.postcode)) {
      g.zip = pr.postcode;
    }

    switch (type) {
      case "house":
        if (str(pr.street)) {
          g.street = pr.street;
          if (str(pr.housenumber)) {
            g.number = pr.housenumber;
          }
        }
        if (city) g.city = city;
        if (district) g.district = district;
        break;
      case "street":
        if (str(pr.street) || name) {
          g.street = str(pr.street) || name;
        }
        if (city) g.city = city;
        if (district) g.district = district;
        break;
      case "locality":
        if (city) g.city = city;
        if (district) {
          g.district = district;
        } else if (name && name != pr.city) {
          g.district = name;
        }
        break;
      case "district":
        if (city) {
          g.city = city;
          if (name && name != pr.city) {
            g.district = name;
          }
        } else if (name) {
          g.district = name;
        }
        break;
      case "city":
        if (city) {
          g.city = city;
        } else if (name) {
          g.city = name;
        }
        break;
      default:
        this._legacyFields(pr, g);
    }

    g.lat = lat;
    g.lng = lng;

    g.state = pr.state;
    g.country = pr.country;

    g.text = this._geoaddressText(g);

    // ohne Orts- und Strassenangabe den Feature-Namen nur als Beschriftung nutzen
    if (!g.city && !g.street && name) {
      g.text = g.text ? name + ", " + g.text : name;
    }

    if (!g.text) {
      return null;
    }

    return g;
  }

  geocode(params) {
    const cfg = runtime();
    if (!cfg.key) {
      return Promise.resolve([]);
    }

    this.cancelAutocomplete();
    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    params.append("key", cfg.key);

    return this.axios.get(cfg.searchUrl, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (this.axiosRequest !== request) {
        throw cancelledError();
      }

      const results = [];
      const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
      features.forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }).catch(thrown => {
      if (this.isCancelled(thrown)) {
        throw cancelledError();
      }
      console.log(thrown);
      throw thrown;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  autocomplete(q) {
    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);

    return this.geocode(params);
  }

  reverse(lat, lng) {
    lat = Number(lat);
    lng = Number(lng);

    const cfg = runtime();
    if (!cfg.key || !isFiniteNumber(lat) || !isFiniteNumber(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return Promise.resolve(null);
    }

    return this.axios.get(cfg.reverseUrl, {
      timeout: REQUEST_TIMEOUT,
      params: {
        lat: lat,
        lon: lng,
        lang: "de",
        key: cfg.key
      }
    }).then(res => {
      if (res && res.data && Array.isArray(res.data.features) && res.data.features.length > 0) {
        const geoaddress = this._geoaddress(res.data.features[0]);
        if (geoaddress) {
          return geoaddress;
        }
      }
      throw new Error("invalid data");
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
